import { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { CompanyGig } from "model/Gig";
import { Api } from "services/api/api.service";
import { getWorkerImg } from "services/firebase/firebase.service";
import { getApplicationWorkerExternalId } from "./teamPublicDetailsModal.mapper";

interface UseTeamPublicDetailsDataParams {
  readonly isOpen: boolean;
  readonly companyId?: number;
  readonly gigId: number;
}

interface UseTeamPublicDetailsDataResult {
  readonly gigData: CompanyGig | null;
  readonly setGigData: Dispatch<SetStateAction<CompanyGig | null>>;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly avatarUrls: Record<string, string>;
}

export const useTeamPublicDetailsData = (
  params: UseTeamPublicDetailsDataParams,
): UseTeamPublicDetailsDataResult => {
  const [gigData, setGigData] = useState<CompanyGig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [avatarUrls, setAvatarUrls] = useState<Record<string, string>>({});
  const fetchRequestIdRef = useRef(0);
  const avatarRequestIdRef = useRef(0);
  const applications = useMemo(
    () => gigData?.applications ?? [],
    [gigData?.applications],
  );
  const applicationsAvatarKey = useMemo(() => {
    return applications
      .map(getApplicationWorkerExternalId)
      .filter((externalId) => externalId.length > 0)
      .sort()
      .join("|");
  }, [applications]);

  useEffect(() => {
    const companyId = params.companyId;
    if (!params.isOpen || !companyId) {
      setGigData(null);
      setIsLoading(false);
      setIsError(false);
      setAvatarUrls({});
      return;
    }
    let isMounted = true;
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;
    const fetchGigData = async (): Promise<void> => {
      const api = Api();
      setIsLoading(true);
      setIsError(false);
      try {
        const response = await api.company.gig.getGig(companyId, params.gigId);
        if (!isMounted || fetchRequestIdRef.current !== requestId) return;
        setGigData(response.data);
      } catch {
        if (!isMounted || fetchRequestIdRef.current !== requestId) return;
        setIsError(true);
      } finally {
        if (!isMounted || fetchRequestIdRef.current !== requestId) return;
        setIsLoading(false);
      }
    };
    void fetchGigData();
    return () => {
      isMounted = false;
    };
  }, [params.companyId, params.gigId, params.isOpen]);

  useEffect(() => {
    const currentApplications = applications;
    if (currentApplications.length === 0) {
      setAvatarUrls({});
      return;
    }
    let isMounted = true;
    const requestId = avatarRequestIdRef.current + 1;
    avatarRequestIdRef.current = requestId;
    const loadAvatars = async (): Promise<void> => {
      const settled = await Promise.allSettled(
        currentApplications.map(async (application) => {
          const externalId = getApplicationWorkerExternalId(application);
          if (!externalId) {
            return { externalId: "", url: "" };
          }
          try {
            const url = await getWorkerImg(externalId);
            return { externalId, url: url ?? "" };
          } catch {
            return { externalId, url: "" };
          }
        }),
      );
      if (!isMounted || avatarRequestIdRef.current !== requestId) return;
      const nextMap = settled.reduce<Record<string, string>>((acc, result) => {
        if (result.status !== "fulfilled") return acc;
        const entry = result.value;
        if (entry.externalId && entry.url) acc[entry.externalId] = entry.url;
        return acc;
      }, {});
      setAvatarUrls(nextMap);
    };
    void loadAvatars();
    return () => {
      isMounted = false;
    };
  }, [applications, applicationsAvatarKey]);

  return { gigData, setGigData, isLoading, isError, avatarUrls };
};
